import {ENTITLEMENT_KEYS} from '@packages/systems/billing/common';
import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {WHEN_NOT_IN_OPTIMIZE_MODE} from './optimization';
import {ONLY_ON_PRIMARY_LOCALE} from './localization';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

export const canAccessAiAssistantWrite = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_AI_ASSISTANT_WRITE],
} as const);

// this shows/hides the CMS button in the left panel
// and allows opening the CMS panel
//
// Campaign sites use CMS collections internally for personalization
// (see CONT-6242), but marketers should never see generic CMS UI
// — the Data Manager modal, Add Collection List flow, right-panel
// binding UI (DynListSettings), Topbar CMS controls, Finder/Chat
// callouts, and Multiplayer presence-avatar navigation all gate on
// this ability. Denying it for `campaign` site kinds closes every
// dispatcher in one place rather than patching each call site.
// See CAM-1237 (parent audit: CAM-1174) and its siblings CAM-1236
// (Topbar) and CAM-1238 (right-panel binding UI).
export const canAccessCMS = defineAbilityConfig({
  abilities: {hasPermissionToAccessCMS: true},
  // View mode is included so a role with CMS item access but no design rights
  // on the current page still reaches the CMS. hasPermissionToAccessCMS is what
  // gates who gets it, not the mode.
  siteModes: {design: true, edit: true, view: true},
  siteKinds: {
    webflow: true,
    tutorial: true,
    cloudapp: true,
    optimize: true,
    campaign: false,
  },
  workflows: {
    canvas: true,
    logicFlows: true,
    dynamicData: true,
    preview: true,
    comment: true,
    optimization: true,
    analyzeVisualView: true,
    webApps: true,
    agents: true,
  },
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
} as const);

// Content Editors that require approval lack site design but keep CMS item manage.
export const hasPermissionToAccessCMS = defineAbilityConfig([
  {
    permission: ['design', 'site'],
  } as const,
  {
    permission: ['manage', 'siteCollectionItem'],
  } as const,
]);

// This allows you to actually save CMS content. (save button in a
// CMS item is disabled). Also blocks related UI (import/export,
// bulk select, etc.
export const canSaveCMSContent = defineAbilityConfig([
  {
    // legacy path (to be removed)
    abilities: {canAccessGranularAccessFeatures: false},
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true, dynamicData: true},
    permission: ['manage', 'siteCollectionItem'],
  } as const,
  {
    abilities: {
      canAccessGranularAccessFeatures: true,
      canEditLocaleContent: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true, dynamicData: true},
    authorizations: {'SiteCollectionItem/manage': true},
  } as const,
]);

export const canAccessCMSSettings = defineAbilityConfig({
  workflows: {canvas: true, dynamicData: true},
  siteModes: {design: true},
} as const);

export const canManageCMSSettings = defineAbilityConfig([
  {
    // legacy path (to be removed)
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true, dynamicData: true},
    siteModes: {design: true},
    permission: ['manage', 'siteCollection'],
  } as const,
  // authorizations path
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true, dynamicData: true},
    siteModes: {design: true},
    authorizations: {'SiteCollection/manage': true},
  } as const,
]);

// Collection-schema gate (e.g. creating a field during CSV import). Permission-
// first so it also applies in edit mode. The granular branch keeps the role gate
// because SiteCollection/manage without a collection scope evaluates permissive.
export const canManageCollectionSchema = defineAbilityConfig([
  {
    abilities: {canAccessGranularAccessFeatures: false},
    permission: ['manage', 'siteCollection'],
  },
  {
    abilities: {canAccessGranularAccessFeatures: true},
    permission: ['manage', 'siteCollection'],
    authorizations: {'SiteCollection/manage': true},
  },
] as const);

// This blocks queue-for-publish in CMS and Ecomm items.
export const canQueueCMSItems = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  workflows: {canvas: true, dynamicData: true},
  permissions: [
    ['queue', 'siteCollectionItem'],
    ['queue', 'siteCommerceItem'],
  ],
} as const);

// This blocks single-item publish in the CMS and Ecomm items.
export const canPublishCMSItems = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  workflows: {canvas: true, dynamicData: true},
  permissions: [
    ['publish', 'siteCollectionItem'],
    ['publish', 'siteCommerceItem'],
  ],
} as const);

export const canCreateCMSCollection = defineAbilityConfig([
  {
    // legacy path (to be removed)
    abilities: {canAccessGranularAccessFeatures: false},
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true, dynamicData: true},
    permission: ['manage', 'siteCollection'],
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  } as const,
  {
    // authorizations path
    abilities: {canAccessGranularAccessFeatures: true},
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true, dynamicData: true}, // collection creation is a site-level action; keep the role gate explicit
    // until this ability can safely rely on non-resource-scoped authorizations
    permission: ['manage', 'siteCollection'],
    authorizations: {'SiteCollection/manage': true},
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  } as const,
]);

/**
 * Can the user connect or disconnect dynamic (CMS) data from elements
 */
export const canConnectDynamicBindings = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {design: true},
    // Campaign sites use CMS internally for personalization, but do not expose
    // generic CMS binding controls.
    siteKinds: {
      webflow: true,
      tutorial: true,
      cloudapp: true,
      optimize: true,
      campaign: false,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  },
  {
    userTypes: {unauthorized: true},
    siteKinds: {
      webflow: true,
      tutorial: true,
      cloudapp: true,
      optimize: true,
      campaign: false,
    },
  },
] as const);

/**
 * Can the user navigate from a page to the corresponding entry in the CMS manager (currently canvas only)
 */
export const canNavigateFromPageToCMS = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  workflows: {canvas: true, comment: false, preview: false},
} as const);

/**
 * @deprecated use cannotManageSiteCollection instead
 */
export const cannotManageCollection = defineAbilityConfig({
  authorizations: {SiteCollection: false},
} as const);

export const canManageSiteCollection = defineAbilityConfig({
  abilities: {
    canAccessGranularAccessFeatures: true,
    canEditLocaleContent: true,
  },
  authorizations: {'SiteCollection/manage': true},
} as const);

export const cannotManageSiteCollection = defineAbilityConfig({
  abilities: {
    canAccessGranularAccessFeatures: true,
  },
  authorizations: {'SiteCollection/manage': false},
} as const);

export const canOnlyReadCMSCollection = defineAbilityConfig([
  {
    abilities: {canAccessGranularAccessFeatures: true},
    authorizations: {
      'SiteCollection/read': true,
      'SiteCollection/manage': false,
      'SiteCollection/create_items': false,
    },
  },
  {
    abilities: {
      canAccessGranularAccessFeatures: true,
      canEditLocaleContent: false,
    },
  },
] as const);

/**
 * Determines whether users can access CMS AI features (field content generation, sample data, etc.)
 * based on AI governance, which consists of a `copilotEnabled` flag and a workspace-level toggle.
 *
 * @see https://webflow.atlassian.net/wiki/spaces/TS/pages/2194538522/AI+features+governance+developer+guide
 */
export const canAccessCmsAIFeatures = defineAbilityConfig({
  abilities: {canAccessAIFeatures: true},
} as const);

/**
 * Determines whether CMS autosave is enabled.
 * Only enabled if the user can save CMS content
 * and has any CMS collection item open.
 */
export const canAutosaveInCMS = defineAbilityConfig({
  abilities: {canSaveCMSContent: true},
  cms: {
    collectionType: 'cms',
    itemType: ['base-item', 'inline-item'],
  },
} as const);

/**
 * Determines whether CMS undo/redo is enabled.
 * Only enabled if the user can save CMS content
 * and has a CMS collection base item open.
 */
export const canUndoRedoInCMS = defineAbilityConfig({
  abilities: {canSaveCMSContent: true},
  cms: {
    collectionType: 'cms',
    itemType: ['base-item'],
  },
} as const);

/**
 * Determines whether CMS undo/redo should record events that occur when an inline item is active.
 * Only enabled if the user can save CMS content
 * and has a CMS collection inline item open.
 */
export const canRecordUndoRedoInCMSInlineItem = defineAbilityConfig({
  abilities: {canSaveCMSContent: true},
  cms: {
    collectionType: 'cms',
    itemType: ['inline-item'],
  },
} as const);

/**
 * Determines whether the user can add static components in static rich text elements.
 * This will show/hide the Component menu item in the rich text's add menu.
 */
export const canAddComponentsInStaticRichText = defineAbilityConfig([
  {
    siteModes: {design: true},
    workflows: {canvas: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    featureFlags: {
      // The addition and deletion of components across users/modes may overwrite changes outside of realtime.
      [FEATURE_FLAGS.REALTIME]: true,
    },
  },
  {
    abilities: {isInPageBuildMode: true},
    workflows: {canvas: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    featureFlags: {
      // The addition and deletion of components across users/modes may overwrite changes outside of realtime.
      [FEATURE_FLAGS.REALTIME]: true,
    },
  },
] as const);

/**
 * Determines whether the user can add components in dynamic (bound) rich text elements.
 * This will show/hide the Component menu item in the rich text's add menu for bound rich text.
 */
export const canAddComponentsInDynamicRichText = defineAbilityConfig([
  {
    siteModes: {design: true},
    workflows: {canvas: true, dynamicData: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    branches: {main: true},
    featureFlags: {
      // The addition and deletion of components across users/modes may overwrite changes outside of realtime.
      [FEATURE_FLAGS.REALTIME]: true,
    },
  },
  {
    abilities: {isInPageBuildMode: true},
    workflows: {canvas: true, dynamicData: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    branches: {main: true},
    featureFlags: {
      // The addition and deletion of components across users/modes may overwrite changes outside of realtime.
      [FEATURE_FLAGS.REALTIME]: true,
    },
  },
] as const);

/**
 * Determines whether the user can add components in a rich text field.
 * True if either canAddComponentsInStaticRichText or canAddComponentsInDynamicRichText is enabled.
 */
export const canAddComponentsInRichText = defineAbilityConfig([
  {
    abilities: {canAddComponentsInStaticRichText: true},
  },
  {
    abilities: {canAddComponentsInDynamicRichText: true},
  },
] as const);

/**
 * Determines whether shared workspace library components can be added into rich text.
 * Can replace with just canViewWorkspaceLibrariesComponents once feature flag is cleaned up.
 */
export const canAddLibraryComponentsInRichText = defineAbilityConfig([
  {
    abilities: {canViewWorkspaceLibrariesComponents: true},
    featureFlags: {
      [FEATURE_FLAGS.CMS_LIBRARY_COMPONENTS_IN_RICH_TEXT]: true,
    },
  },
] as const);

/**
 * Determines whether the user can access improvements to the integration
 * between the data manager and the canvas.
 */
export const canAccessDMCanvasIntegration = defineAbilityConfig([
  {
    featureFlags: {
      [FEATURE_FLAGS.CMS_DM_CANVAS_INTEGRATION]: true,
    },
  },
] as const);

/**
 * Determines whether the user can use bulk indexing operations in the CMS Data Manager
 * through the Bulk Actions toolbar, allowing users to filter items by indexing status and
 * update item indexing status in bulk.
 */
export const canUseCMSBulkIndexing = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.CMS_INDEXING_BULK_ACTIONS]: true,
  },
} as const);

/**
 * Determines whether the user can organize Custom fields into named field
 * groups (rendered as collapsible sections in the UI) in Collection Settings.
 * Restricted to CMS collections.
 */
export const canUseCMSFieldGroups = defineAbilityConfig({
  cms: {
    collectionType: 'cms',
  },
} as const);
